import { Component, OnInit } from '@angular/core';
import { ChatService, Thread } from './chats.service';
import { combineLatest, from, map, of, Subscription, switchMap } from 'rxjs';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';

@Component({
  selector: 'app-chats',
  templateUrl: './chats.page.html',
  styleUrls: ['./chats.page.scss'],
})
export class ChatsPage implements OnInit {
  me!: string;
  threads: Array<Thread & { otherUid: string; otherName: string; unreadMine: number }> = [];
  sub?: Subscription;
  isLoading = true;
  isSignedIn = false;
  constructor(
    private afAuth: AngularFireAuth,
    private chatService: ChatService,
    private afs: AngularFirestore,
    private router: Router,
    private alertCtrl: AlertController
  ) { }

  async ngOnInit() {
    const u = await this.getUser();
    if (!u) {
      this.isLoading = false;
      this.isSignedIn = false
      return;
    }
    this.me = u.uid;
    this.isSignedIn = true;
    this.sub = this.chatService.observeThreadsList(this.me).pipe(
      switchMap(ts => {
        if (ts.length === 0) return of([] as Array<Thread & { otherUid: string; otherName: string; unreadMine: number }>);
        const observables = ts.map(t => {
          const otherUid = t.participants[0] === this.me ? t.participants[1] : t.participants[0];

          return this.chatService.observeUser(otherUid).pipe(
            // decide name source
            switchMap(uDoc => {
              const nameFromFs = uDoc?.displayName?.trim();
              if (nameFromFs) {
                return of(nameFromFs);
              }
              // fall back to your API (assumed Promise<string | null | undefined>)
              return from(this.chatService.getUsernameById(otherUid)).pipe(
                map(n => (n?.trim() || 'User'))
              );
            }),
            map(otherName => ({
              ...t,
              otherUid,
              otherName,
              unreadMine: t.unread?.[this.me] ?? 0,
            }))
          );
        });

        // combine all per-thread streams into one array of rows
        return combineLatest(observables);
      })
    ).subscribe(list => {
      this.threads = list;
      this.isLoading = false;
    });
  }

  ngOnDestroy() { if (this.sub) this.sub.unsubscribe(); }

  open(tid: string) {
    this.router.navigate(['chats', 'chat', tid]);
  }

  time(ms?: number) {
    if (!ms) return '';
    const today = new Date();
    const d = new Date(ms);
    const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const isToday = dDate.getTime() === todayDate.getTime();
    const locale = 'en-US';
    if (isToday) {
      return d.toLocaleString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // Set to false for 24-hour time
      });
    } else {
      return d.toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    }
  }

  initials(name: string) {
    const s = (name || 'User').trim();
    return s ? s.slice(0, 1).toUpperCase() : 'U';
  }

  async getUser() {
    let user = await this.afAuth.currentUser;
    if (!user) {
      user = await new Promise(resolve => {
        const sub = this.afAuth.authState.subscribe(u => {
          resolve(u ?? null);
          sub.unsubscribe();
        });
      });
    }
    return user ?? null;
  }

  async hideChat(tid) {
    const alert = await this.alertCtrl.create({
      header: 'Hide chat?',
      message: 'This hides the conversation from your inbox. The other person will still keep their copy.',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Hide Chat',
          role: 'destructive',
          handler: async () => {
            await this.chatService.deleteThreadForMe(tid);
          }
        }
      ]
    });
    await alert.present();
  }
}
